import React, { useEffect, useState } from 'react';
import { adminAPI } from '../../lib/adminAPI';
import { useToast } from '../../hooks/use-toast';
import { Sliders, Plus, Trash2, Save } from 'lucide-react';

const AdminSettings = () => {
  const [globalPips, setGlobalPips] = useState('0.20');
  const [overrides, setOverrides] = useState([]); // [{symbol, pips}]
  const [newSym, setNewSym] = useState('');
  const [newPips, setNewPips] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();

  const load = async () => {
    setLoading(true);
    try {
      const cfg = await adminAPI.getSpread();
      setGlobalPips(String(cfg.global_pips ?? 0.20));
      setOverrides(Object.entries(cfg.overrides || {}).map(([symbol, pips]) => ({ symbol, pips: String(pips) })));
    } catch {}
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const addOverride = () => {
    const sym = newSym.trim().toUpperCase();
    const p = parseFloat(newPips);
    if (!sym) { toast({ title: 'Enter a symbol', description: 'e.g. EURUSD, BTCUSDT, XAUUSD' }); return; }
    if (Number.isNaN(p) || p < 0) { toast({ title: 'Invalid pips' }); return; }
    if (overrides.some(o => o.symbol === sym)) { toast({ title: 'Symbol already has an override' }); return; }
    setOverrides([...overrides, { symbol: sym, pips: String(p) }]);
    setNewSym(''); setNewPips('');
  };

  const removeOverride = (sym) => setOverrides(overrides.filter(o => o.symbol !== sym));

  const save = async () => {
    const g = parseFloat(globalPips);
    if (Number.isNaN(g) || g < 0) { toast({ title: 'Invalid global spread' }); return; }
    const ov = {};
    for (const o of overrides) {
      const p = parseFloat(o.pips);
      if (!Number.isNaN(p) && p >= 0) ov[o.symbol] = p;
    }
    setSaving(true);
    try {
      await adminAPI.setSpread({ global_pips: g, overrides: ov });
      toast({ title: 'Spread settings saved' });
      load();
    } catch (e) {
      toast({ title: 'Save failed', description: e?.response?.data?.detail || 'Try again' });
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-6" data-testid="admin-settings-page">
      <div>
        <h1 className="text-2xl font-bold text-white">Trading Settings</h1>
        <p className="text-sm text-gray-400">Control the account spread applied to every order fill. Buy fills at ask, Sell at bid.</p>
      </div>

      <div className="rounded-xl border border-[#1a2823] bg-[#0a1210] overflow-hidden max-w-2xl">
        <div className="px-4 py-3 border-b border-[#141f1b] flex items-center gap-2">
          <Sliders size={14} className="text-[#00e676]"/>
          <span className="text-sm font-semibold text-white">Spread (in pips)</span>
        </div>

        {loading ? (
          <div className="py-10 text-center text-gray-500 text-sm">Loading…</div>
        ) : (
          <div className="p-5 space-y-6">
            {/* Global */}
            <div>
              <label className="text-[10px] text-gray-400 uppercase tracking-wider">Global default spread</label>
              <div className="mt-1.5 flex items-center gap-2">
                <input
                  type="number" step="0.01" min="0" value={globalPips}
                  onChange={e => setGlobalPips(e.target.value)}
                  data-testid="global-spread-input"
                  className="w-40 bg-[#050807] border border-[#1a2823] rounded-lg px-3 py-2.5 text-white text-sm mono focus:outline-none focus:border-[#00e676]"
                />
                <span className="text-xs text-gray-500">pips (applies to any symbol without an override)</span>
              </div>
            </div>

            {/* Overrides */}
            <div>
              <label className="text-[10px] text-gray-400 uppercase tracking-wider">Per-symbol overrides</label>
              <div className="mt-2 space-y-2">
                {overrides.length === 0 && <div className="text-xs text-gray-600">No overrides. All symbols use the global default.</div>}
                {overrides.map(o => (
                  <div key={o.symbol} className="flex items-center gap-2" data-testid={`override-row-${o.symbol}`}>
                    <span className="w-32 text-sm text-white font-semibold mono">{o.symbol}</span>
                    <input
                      type="number" step="0.01" min="0" value={o.pips}
                      onChange={e => setOverrides(overrides.map(x => x.symbol === o.symbol ? { ...x, pips: e.target.value } : x))}
                      className="w-28 bg-[#050807] border border-[#1a2823] rounded-lg px-3 py-2 text-white text-sm mono focus:outline-none focus:border-[#00e676]"
                    />
                    <span className="text-xs text-gray-500">pips</span>
                    <button onClick={() => removeOverride(o.symbol)} data-testid={`remove-override-${o.symbol}`} className="ml-auto p-1.5 rounded border border-[#1a2823] text-red-400 hover:border-red-500/50"><Trash2 size={13}/></button>
                  </div>
                ))}
              </div>

              <div className="mt-3 flex items-center gap-2 border-t border-[#141f1b] pt-3">
                <input
                  value={newSym} onChange={e => setNewSym(e.target.value)} placeholder="Symbol (e.g. EURUSD)"
                  data-testid="new-override-symbol"
                  className="w-40 bg-[#050807] border border-[#1a2823] rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#00e676]"
                />
                <input
                  type="number" step="0.01" min="0" value={newPips} onChange={e => setNewPips(e.target.value)} placeholder="pips"
                  data-testid="new-override-pips"
                  className="w-28 bg-[#050807] border border-[#1a2823] rounded-lg px-3 py-2 text-white text-sm mono focus:outline-none focus:border-[#00e676]"
                />
                <button onClick={addOverride} data-testid="add-override-btn" className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-[#1a2823] text-gray-300 hover:text-[#00e676] hover:border-[#00e676]/50 text-sm"><Plus size={14}/> Add</button>
              </div>
            </div>

            <button onClick={save} disabled={saving} data-testid="save-spread-btn" className="btn-green flex items-center gap-2 px-5 py-2.5 rounded-lg font-semibold text-sm disabled:opacity-50">
              <Save size={15}/> {saving ? 'Saving…' : 'Save Settings'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminSettings;
